import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import type { ConsentPreferences } from "@/lib/consent/consentTypes";

type CookiePreferencesDialogProps = {
  open: boolean;
  preferences: ConsentPreferences | null;
  onOpenChange: (open: boolean) => void;
  onAcceptAll: () => void;
  onRejectOptional: () => void;
  onSaveChoices: (values: Pick<ConsentPreferences, "analytics" | "advertising">) => void;
};

export default function CookiePreferencesDialog({
  open,
  preferences,
  onOpenChange,
  onAcceptAll,
  onRejectOptional,
  onSaveChoices,
}: CookiePreferencesDialogProps) {
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);

  useEffect(() => {
    if (open) {
      setAnalytics(preferences?.analytics ?? false);
      setAdvertising(preferences?.advertising ?? false);
    }
  }, [open, preferences]);

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Your privacy choices</DialogTitle>
          <DialogDescription>
            Manage optional analytics and advertising technologies. Necessary technologies always stay enabled.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <PreferenceRow
            checked
            description="Supports security, authentication, checkout continuity, saved case access, and core site functions."
            disabled
            label="Necessary"
          />
          <PreferenceRow
            checked={analytics}
            description="Helps us understand how JusticeDrive is used, which pages are helpful, and where product flows need improvement."
            label="Analytics"
            onCheckedChange={setAnalytics}
          />
          <PreferenceRow
            checked={advertising}
            description="Allows Google Ads and Microsoft Advertising measurement and remarketing signals for campaign performance."
            label="Advertising"
            onCheckedChange={setAdvertising}
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button onClick={onRejectOptional} type="button" variant="outline">
            Reject optional
          </Button>
          <Button onClick={() => onSaveChoices({ analytics, advertising })} type="button" variant="secondary">
            Save choices
          </Button>
          <Button onClick={onAcceptAll} type="button">
            Accept all
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreferenceRow({
  checked,
  description,
  disabled = false,
  label,
  onCheckedChange,
}: {
  checked: boolean;
  description: string;
  disabled?: boolean;
  label: string;
  onCheckedChange?: (checked: boolean) => void;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card/80 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-bold text-foreground">{label}</h3>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">{description}</p>
        </div>
        <Switch
          aria-label={`${label} cookies`}
          checked={checked}
          disabled={disabled}
          onCheckedChange={onCheckedChange}
        />
      </div>
    </section>
  );
}
